import { LoadingOutlined, TeamOutlined } from '@ant-design/icons';
import { AuthRoles } from '@hefe/auth';
import { t } from '@hefe/i18n';
import {
  IssuesApi,
  ModalsSlice,
  OpenDialogActionPayload,
  getChildUser,
  logoutUser,
  updateBasketballUserCoachTeam,
  userState,
} from '@hefe/store';
import { colors } from '@hefe/ui';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Drawer,
  Dropdown,
  Layout,
  Menu,
  MenuProps,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { FaStar } from 'react-icons/fa';
import {
  MdOutlineNotificationsActive,
  MdOutlineNotificationsNone,
} from 'react-icons/md';
import { useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import headericon from '../../../assets/images/logo/headericon.png';
import { APP_DATA } from '../../../version';
import UserAvatar from '../../containers/core/components/user/user-avatar/user-avatar/user-avatar.component';
import IssuesDrawerComponent from '../../containers/issues/components/issues-drawer/issues-drawer.component';
import NotificationsListActionsComponent from '../../containers/notifications/components/notifications-list-actions/notifications-list-actions.component';
import NotificationsListComponent from '../../containers/notifications/components/notifications-list/notifications-list.component';
import SmartAssistantTopbarButton from '../../containers/smart-assistant/components/smart-assistant-topbar-btn/smart-assistant-topbar-btn.component';
import basketballNav, { NavItem } from '../../navigation/basketball.nav';
import basketballTopNav from '../../navigation/basketball.top.nav';
import { useAppDispatch } from '../../store/store';
import styles from './default-layout.module.scss';

type MenuItem = Required<MenuProps>['items'][number];

/* eslint-disable-next-line */
export interface DefaultLayoutProps {}

export function DefaultLayout(props: DefaultLayoutProps) {
  const { Header, Content, Footer, Sider } = Layout;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const stateUser = useSelector(userState);
  const location = useLocation();

  const [wrapOutlet, setWrapOutlet] = useState(false);
  const [changingTeam, setChangingTeam] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [issuesOpen, setIssuesOpen] = useState(false);

  const userIsAdmin = !!stateUser.user?.role.some((r) =>
    AuthRoles.bbadmin.includes(r)
  );

  // Loads once per authenticated session; shared RTK Query cache also feeds the court vision drawer
  const { data: issuesOverview } = IssuesApi.useFetchIssuesOverviewQuery(
    undefined,
    {
      skip: !stateUser.user || !userIsAdmin,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  const noCardWrapper = new Set(['/', '/dashboard']);
  useEffect(() => {
    //setWrapOutlet(!noCardWrapper.has(location.pathname));
  }, [location.pathname]);

  const user = stateUser.user!;
  const childUsers = stateUser.childUsers;

  const userDropdownMenu = useMemo(() => {
    const returnValue: any[] = [];

    basketballTopNav?.items?.map((item, index) => {
      returnValue.push({
        key: index,
        label: t(item.name),
        onClick: () => navigate(item.url),
      });
    });

    // Tasks list
    returnValue.push({
      key: 'tasks',
      label: (
        <Badge count={user?.open_tasks_count} offset={[10, 0]}>
          {t('tasks.plural')}
        </Badge>
      ),
      onClick: () => navigate('/user/tasks'),
    });

    // Notifications
    let Icon = MdOutlineNotificationsNone;
    let shakeClass = '';

    if (user?.notifications?.unread?.length > 0) {
      Icon = MdOutlineNotificationsActive;
      shakeClass = styles['icon-shake'];
    }

    returnValue.push({
      key: 'notifications',
      label: t('notifications.plural'),
      icon: <Icon className={shakeClass} />,
      onClick: () => setNotificationsOpen(true),
    });
    // Logout
    returnValue.push({
      key: 'logout',
      label: 'Logout',
      onClick: () => {
        setLoggingOut(true);
        dispatch(logoutUser({}))
          .unwrap()
          .then(() => {
            navigate('/login');
          })
          .finally(() => setLoggingOut(false));
      },
    });

    return returnValue;
  }, [user, basketballTopNav, notificationsOpen]);

  let username = '';
  if (user) {
    username = user.firstname + ' ' + user.lastname;
  } else {
    return null;
  }

  const onTeamClick = (teamId: number) => {
    setChangingTeam(true);
    dispatch(updateBasketballUserCoachTeam(teamId))
      .unwrap()
      .then(() => {
        message.success(t('information_texts.team_changed'));
      })
      .catch(() => null)
      .finally(() => setChangingTeam(false));
  };

  const getNavigationItemsRecursive = (items: NavItem[]) => {
    const returnValue: MenuItem[] = [];

    if (!user) {
      return [];
    }

    for (const item of items) {
      const allowed = user.role.some((r) => item.auth?.includes(r));
      const userIsAdmin = user.role.some((r) => AuthRoles.bbadmin.includes(r));
      const allowedClubSubscription = item.subscription?.includes(
        user?.club_subscription?.active
      );
      const activeTeamID = user.basketball_user!.active_coach_team_id;
      const teamAllowed = item.teamSection ? activeTeamID !== null : true;
      let generalFunctionAllowed = true;
      if (item?.generalClubFunctionType) {
        generalFunctionAllowed = _.get(
          user,
          'club_settings.general.functions.' + item.generalClubFunctionType,
          false
        );
      }

      if (
        !allowed ||
        !teamAllowed ||
        (!allowedClubSubscription && !userIsAdmin) ||
        !generalFunctionAllowed
      ) {
        continue;
      }

      const icon = item.title ? item.icon : <i className={item.icon} />;
      let title = item.userSection ? username : item.name;

      if (item.teamSection) {
        const team = user.basketball_user?.coached_teams?.find(
          (t) => t.id === user.basketball_user?.active_coach_team_id
        );
        if (team) {
          title = team.name;
        }
      }

      if (item.children) {
        returnValue.push({
          key: item.url,
          label: ' ' + t(title).toUpperCase(),
          children: getNavigationItemsRecursive(item.children),
        });
      } else {
        const showUpgrade = !allowedClubSubscription && userIsAdmin;

        returnValue.push({
          key: item.url,
          label: showUpgrade ? (
            <Tooltip
              arrow={false}
              title={t(`errors.permissions.${item.subscription[0]}`)}
            >
              <Space size={8}>
                <Typography.Text style={{ color: colors.lightgrey }}>
                  {t(item.name)}
                </Typography.Text>
                <Tag color={colors.darkgrey}>
                  <FaStar size={12} />
                </Tag>
              </Space>
            </Tooltip>
          ) : (
            t(item.name)
          ),
          onClick: () => navigate(item.url),
          disabled: item.disabled || showUpgrade,
        });
      }
    }

    return returnValue;
  };

  const renderTeamSelection = () => {
    let activeTeamName = t('no_team');
    const Icon = changingTeam ? <LoadingOutlined /> : <TeamOutlined />;

    if (
      !user?.basketball_user?.coached_teams ||
      user?.basketball_user?.coached_teams?.length === 0
    ) {
      return null;
    }

    const team = user.basketball_user?.coached_teams?.find(
      (t) => t.id === user.basketball_user?.active_coach_team_id
    );
    if (team) {
      activeTeamName = team.name;
    }

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginRight: 10,
          cursor: 'pointer',
        }}
      >
        <Dropdown
          disabled={changingTeam}
          trigger={['click']}
          menu={{
            activeKey:
              user.basketball_user?.active_coach_team_id + '' || undefined,
            items: user.basketball_user?.coached_teams?.map((team) => {
              return {
                key: team.id,
                label: team.name,
                onClick: () => onTeamClick(team.id),
              };
            }),
          }}
        >
          <Tag icon={Icon} color={changingTeam ? colors.grey : colors.primary}>
            {activeTeamName}
          </Tag>
        </Dropdown>
      </div>
    );
  };

  const renderIssueSection = () => {
    if (!userIsAdmin) {
      return;
    }

    return (
      <SmartAssistantTopbarButton
        issueCount={issuesOverview?.unacknowledged_total}
        onClick={() => setIssuesOpen(true)}
      />
    );
  };

  const renderChildUsers = () => {
    if (childUsers.length === 0) {
      return null;
    }

    const usersOutput: any = [];

    childUsers.forEach((user) => {
      const username = user.firstname + ' ' + user.lastname;
      usersOutput.push(
        <Tooltip key={user.id} title={username}>
          <div
            key={user.id}
            className={styles['child-user-bubble']}
            onClick={() => {
              const payload: OpenDialogActionPayload = {
                title: t('switch_account'),
                text: t('questions.switch_account', user),
                onOk: (closeDialog: () => void) => {
                  dispatch(getChildUser({ child_id: user.id }))
                    .unwrap()
                    .then(() => {
                      window.location.reload();
                    })
                    .catch(() => null);
                },
              };

              dispatch(ModalsSlice.modalsActions.openDialog(payload));
            }}
          >
            <UserAvatar user={user} />
          </div>
        </Tooltip>
      );
    });

    return (
      <Avatar.Group
        size="small"
        style={{
          marginLeft: 0,
          marginRight: -10,
          marginTop: 5,
          zIndex: 1,
        }}
      >
        {usersOutput}
      </Avatar.Group>
    );
  };

  const renderUserMenu = () => {
    if (loggingOut) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin />
        </div>
      );
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          //alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
        }}
      >
        {renderIssueSection()}
        {renderTeamSelection()}
        {renderChildUsers()}
        <Dropdown
          className={styles['user-dropdown']}
          trigger={['click']}
          menu={{ items: userDropdownMenu }}
        >
          <Space align="center">
            <Badge
              count={user.notifications?.unread?.length}
              overflowCount={99}
            >
              <UserAvatar size={28} user={user} />
            </Badge>
            <Typography.Text>{username}</Typography.Text>
          </Space>
        </Dropdown>
      </div>
    );
  };

  return (
    <Layout className="site-layout" style={{ minHeight: '100vh' }}>
      <Header className={styles['layout-header']}>
        <div className={styles['header-icon']}>
          <img alt="header-icon" src={headericon} />
        </div>
        {renderUserMenu()}
      </Header>
      <Layout>
        <Sider
          className="main-menu"
          theme="light"
          width={250}
          breakpoint="lg"
          collapsedWidth="0"
          onBreakpoint={(broken: any) => null}
          onCollapse={(collapsed: boolean, type: any) => null}
        >
          <div className="logo" />
          <Menu
            mode="inline"
            defaultSelectedKeys={['1']}
            items={getNavigationItemsRecursive(basketballNav.items)}
          />
        </Sider>
        <Content style={{ margin: '24px 16px 0' }}>
          {wrapOutlet ? (
            <Card>
              <Outlet />
            </Card>
          ) : (
            <Card>
              <Outlet />
            </Card>
          )}
        </Content>
      </Layout>
      <Footer style={{ textAlign: 'center' }}>
        <Space size={8}>
          <Typography.Text>©2020-2026 Backcourt</Typography.Text>
          <Typography.Text>|</Typography.Text>
          <Typography.Text>
            <a href="/support">{t('help')}</a>
          </Typography.Text>
          <Typography.Text>|</Typography.Text>
          <Button type="link" onClick={() => navigate('/system/versions')}>
            {APP_DATA.version} ({APP_DATA.buildNumber})
          </Button>
        </Space>
      </Footer>
      <Drawer
        title={t('notifications.plural')}
        extra={<NotificationsListActionsComponent />}
        placement="right"
        key="notifications"
        onClose={() => setNotificationsOpen(false)}
        open={notificationsOpen}
      >
        <NotificationsListComponent user={user} />
      </Drawer>
      <Drawer
        title={t('issues.court_vision.title')}
        placement="right"
        key="issues"
        onClose={() => setIssuesOpen(false)}
        open={issuesOpen}
      >
        <IssuesDrawerComponent />
      </Drawer>
    </Layout>
  );
}

export default DefaultLayout;
